import React, { useContext, useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { useHistory } from "react-router-dom";
import { Avatar, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, FormControlLabel, IconButton, MenuItem, TextField, Typography, makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import CloseIcon from "@material-ui/icons/Close";
import SyncIcon from "@material-ui/icons/Sync";
import SendIcon from "@material-ui/icons/Send";
import HistoryIcon from "@material-ui/icons/History";
import { toast } from "react-toastify";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import ContactAvatar from "../ContactAvatar";
import { contactLabel, contactDisplayName, contactNumber, mergeContact } from "../../utils/contactPresentation";

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 20, border: `1px solid ${theme.custom.border}`, width: 620, margin: 16 },
  header: { padding: "22px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: `1px solid ${theme.custom.border}`, background: theme.palette.type === "dark" ? "rgba(16,185,129,.08)" : "#F0FDF8" },
  icon: { background: "rgba(16,185,129,.15)", color: theme.palette.primary.main, width: 46, height: 46, borderRadius: 14 },
  title: { fontWeight: 800, fontSize: "1.15rem" },
  subtitle: { fontSize: ".8rem", color: theme.palette.text.secondary, marginTop: 3 },
  close: { marginLeft: "auto", alignSelf: "flex-start" },
  content: { padding: 24, display: "flex", flexDirection: "column", gap: 20 },
  search: { display: "flex", flexDirection: "column", gap: 6 },
  sync: { alignSelf: "flex-start", fontSize: ".72rem" },
  option: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  optionName: { fontWeight: 600, overflowWrap: "anywhere" },
  optionNumber: { fontSize: ".75rem", color: theme.palette.text.secondary },
  history: { padding: "12px 16px", borderRadius: 12, border: `1px solid ${theme.custom.border}`, background: theme.palette.background.default },
  historyLabel: { fontSize: ".82rem", fontWeight: 600 },
  note: { fontSize: ".76rem", color: theme.palette.text.secondary, lineHeight: 1.6 },
  error: { padding: "12px 14px", borderRadius: 10, background: theme.palette.type === "dark" ? "rgba(239,68,68,.12)" : "#FEF2F2", color: theme.palette.error.main, fontSize: ".82rem" },
  notice: { fontSize: ".78rem", color: theme.palette.text.secondary },
  actions: { padding: "16px 24px", borderTop: `1px solid ${theme.custom.border}`, justifyContent: "space-between", flexWrap: "wrap", gap: 8 },
}));

const errorMessages = {
  ERR_DIRECT_MESSAGE_CONTACT_NUMBER_UNAVAILABLE: "Este contato ainda não tem um telefone identificado. Digite o número real com código do país e DDD para iniciar a conversa.",
  ERR_DIRECT_MESSAGE_CONNECTION_REQUIRED: "Selecione o WhatsApp que enviará a mensagem.",
  ERR_DIRECT_MESSAGE_CONNECTION_OFFLINE: "Este WhatsApp está desconectado. Reconecte-o em Conexões e tente novamente.",
  ERR_DIRECT_MESSAGE_RECIPIENT_REQUIRED: "Selecione um contato ou informe o número com código do país e DDD.",
  ERR_DIRECT_MESSAGE_INVALID_NUMBER: "Confira o número, incluindo o código do país e o DDD.",
  ERR_DIRECT_MESSAGE_BODY_REQUIRED: "Escreva a mensagem que deseja enviar.",
  ERR_DIRECT_MESSAGE_INVALID_REQUEST: "Confira os dados da mensagem antes de enviar.",
  ERR_DIRECT_MESSAGE_GROUP_NOT_SUPPORTED: "Selecione um contato individual para iniciar uma mensagem direta.",
  ERR_DIRECT_MESSAGE_NUMBER_CHECK_FAILED: "Não foi possível consultar o número no WhatsApp. Confira a conexão e tente novamente.",
  ERR_NUMBER_NOT_ON_WHATSAPP: "Este número não foi encontrado no WhatsApp. Confira o código do país e o DDD.",
  ERR_DIRECT_MESSAGE_TICKET_UNAVAILABLE: "Este atendimento não está mais disponível.",
  ERR_DIRECT_MESSAGE_REQUEST_CONFLICT: "Esta tentativa já foi usada para outra mensagem. Edite a mensagem antes de enviar novamente.",
  ERR_DIRECT_MESSAGE_IN_PROGRESS: "O envio anterior ainda está em andamento. Aguarde alguns segundos antes de consultar novamente.",
  ERR_DIRECT_MESSAGE_SEND_UNCONFIRMED: "O WhatsApp não confirmou o envio. Abra o atendimento e confira a conversa antes de tentar outra mensagem.",
  ERR_TICKET_ASSIGNED_TO_ANOTHER_USER: "Este cliente já está em atendimento com outra pessoa. Solicite a transferência antes de enviar uma mensagem.",
  ERR_WAPP_NOT_INITIALIZED: "A conexão está iniciando. Aguarde e tente novamente.",
  ERR_WAPP_NOT_CONNECTED: "Este WhatsApp está desconectado. Reconecte-o em Conexões.",
  ERR_WHATSAPP_NOT_CONNECTED: "Este WhatsApp está desconectado. Reconecte-o em Conexões.",
  ERR_NO_WAPP_FOUND: "A conexão selecionada não está disponível.",
  ERR_INVALID_NUMBER: "Confira o número, incluindo o código do país e o DDD.",
  ERR_INVALID_CONTACT: "Não foi possível encontrar este número no WhatsApp. Confira o código do país e o DDD.",
  ERR_WAPP_INVALID_CONTACT: "Não foi possível encontrar este número no WhatsApp. Confira o código do país e o DDD.",
  ERR_NO_CONTACT_FOUND: "Este contato não está mais disponível. Pesquise novamente.",
  ERR_SENDING_WAPP_MSG: "Não foi possível enviar a mensagem. Confira a conexão e tente novamente.",
};
const explainError = error => {
  const code = error.response?.data?.error || error.response?.data?.message;
  return errorMessages[code] || (code && !code.startsWith("ERR_") ? code : "Não foi possível concluir. Confira a conexão e tente novamente.");
};
const numberFrom = value => /^[+\d\s().-]+$/.test(value) ? value.replace(/\D/g, "") : "";

export default function DirectMessageModal({ open, onClose }) {
  const classes = useStyles();
  const history = useHistory();
  const { whatsApps, loading: loadingConnections } = useContext(WhatsAppsContext);
  const [whatsappId, setWhatsappId] = useState("");
  const [search, setSearch] = useState("");
  const [contact, setContact] = useState(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [syncHistory, setSyncHistory] = useState(true);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [importing, setImporting] = useState(false);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [failedTicketId, setFailedTicketId] = useState(null);
  const requestId = useRef(null);
  const searchGeneration = useRef(0);
  const sendingRef = useRef(false);
  const selectionRef = useRef({ contact: null, search: "" });
  const modalGeneration = useRef(0);
  const selectedConnection = whatsApps.find(item => item.id === Number(whatsappId));
  const connected = selectedConnection?.status === "CONNECTED";
  const number = contact ? "" : numberFrom(search.trim());
  const validNumber = /^[1-9]\d{7,14}$/.test(number);
  const validRecipient = contact ? Boolean(contactNumber(contact)) : validNumber;

  useLayoutEffect(() => { selectionRef.current = { contact, search }; }, [contact, search]);
  useEffect(() => { const generation = ++modalGeneration.current; return () => { modalGeneration.current = generation + 1; }; }, [open]);

  const updateSelectedContact = useCallback(incoming => {
    const current = selectionRef.current.contact;
    if (!current || String(current.id) !== String(incoming.id)) return;
    const merged = mergeContact(current, incoming);
    if (merged === current) return;
    const label = contactLabel(merged);
    selectionRef.current = { contact: merged, search: label };
    searchGeneration.current++;
    setContact(merged); setSearch(label);
  }, []);

  useEffect(() => {
    if (!open) return;
    setSearch(""); setContact(null); setName(""); setBody(""); setSyncHistory(true);
    setOptions([]); setError(""); setNotice(""); setFailedTicketId(null);
    requestId.current = null;
    selectionRef.current = { contact: null, search: "" };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setWhatsappId(current => {
      if (whatsApps.some(item => item.id === Number(current))) return current;
      const available = whatsApps.filter(item => item.status === "CONNECTED");
      return (available.find(item => item.isDefault) || (available.length === 1 ? available[0] : null))?.id || "";
    });
  }, [open, whatsApps]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const generation = ++searchGeneration.current;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { searchParam: contact ? "" : (numberFrom(search.trim()) || search.trim()) } });
        if (active && generation === searchGeneration.current) setOptions(current => (data.contacts || []).filter(item => !item.isGroup).map(item => mergeContact(current.find(option => String(option.id) === String(item.id)), item)));
      } catch (err) {
        if (active && generation === searchGeneration.current) { setOptions([]); setError("Não foi possível pesquisar os contatos. Tente novamente."); }
      } finally {
        if (active && generation === searchGeneration.current) setSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [open, search, contact, revision]);

  useEffect(() => {
    if (!open) return undefined;
    const socket = openSocket();
    socket.on("contact", data => {
      if (data.action === "update" && data.contact) {
        setOptions(current => current.map(option => String(option.id) === String(data.contact.id) ? mergeContact(option, data.contact) : option));
        updateSelectedContact(data.contact);
      }
      if (data.action === "delete") {
        setOptions(current => current.filter(option => String(option.id) !== String(data.contactId)));
        if (String(selectionRef.current.contact?.id) === String(data.contactId)) {
          selectionRef.current = { contact: null, search: "" };
          setContact(null); setSearch(""); requestId.current = null;
          setError("Este contato foi removido. Pesquise novamente ou digite o número.");
        }
      }
    });
    return () => socket.disconnect();
  }, [open, updateSelectedContact]);

  const handleSync = async () => {
    const generation = modalGeneration.current;
    setImporting(true); setError(""); setNotice("");
    try {
      const { data } = await api.post("/contacts/import", { whatsappId: Number(whatsappId) });
      if (generation !== modalGeneration.current) return;
      searchGeneration.current++; setOptions([]);
      const selected = selectionRef.current.contact;
      if (selected?.id) {
        try {
          const { data: freshContact } = await api.get(`/contacts/${selected.id}`);
          if (generation !== modalGeneration.current) return;
          updateSelectedContact(freshContact);
        } catch (_) {
          // Import succeeded; contact events/search can still provide the refreshed record.
        }
      }
      if (generation !== modalGeneration.current) return;
      setNotice(data.total === 0
        ? "O WhatsApp ainda não disponibilizou contatos. Você pode iniciar a conversa digitando o número."
        : `${data.imported || 0} novos contatos · ${data.updated || 0} atualizados. A agenda disponível nesta conexão foi sincronizada.`);
      setRevision(value => value + 1);
    } catch (err) { if (generation === modalGeneration.current) setError(explainError(err)); }
    finally { if (generation === modalGeneration.current) setImporting(false); }
  };

  const handleSend = async event => {
    event.preventDefault();
    if (sendingRef.current || !connected || !validRecipient || !body.trim() || importing) return;
    sendingRef.current = true;
    setSending(true); setError(""); setFailedTicketId(null);
    try {
      if (!requestId.current) {
        requestId.current = window.crypto?.randomUUID ? window.crypto.randomUUID() :
          "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
            const random = Math.floor(Math.random() * 16);
            return (character === "x" ? random : (random & 3) | 8).toString(16);
          });
      }
      const { data } = await api.post("/messages/direct", {
        whatsappId: Number(whatsappId), ...(contact ? { contactId: contact.id } : { number, name: name.trim() || undefined }),
        body: body.trim(), syncHistory, requestId: requestId.current,
      });
      toast.success(data.reused ? "Mensagem enviada. Conversa existente aberta." : "Mensagem enviada e atendimento criado.");
      if (data.history?.message) toast.info(data.history.message, { autoClose: 8000 });
      onClose();
      history.push(`/tickets/${data.ticket.id}`);
    } catch (err) {
      setError(explainError(err));
      if (err.response?.data?.retryable === true) requestId.current = null;
      setFailedTicketId(err.response?.data?.ticketId || null);
    } finally { sendingRef.current = false; setSending(false); }
  };

  const edit = action => value => { action(value); requestId.current = null; setError(""); setFailedTicketId(null); };
  const busy = sending || importing;
  return <Dialog open={open} onClose={() => { if (!busy) onClose(); }} maxWidth="sm" fullWidth classes={{ paper: classes.paper }} aria-labelledby="direct-message-title">
    <form onSubmit={handleSend}>
      <div className={classes.header}>
        <Avatar className={classes.icon}><ChatBubbleOutlineIcon /></Avatar>
        <div><Typography component="h2" id="direct-message-title" className={classes.title}>Mensagem direta</Typography><Typography className={classes.subtitle}>Comece uma conversa pelo WhatsApp.</Typography></div>
        <IconButton size="small" className={classes.close} aria-label="Fechar mensagem direta" disabled={busy} onClick={onClose}><CloseIcon /></IconButton>
      </div>
      <DialogContent className={classes.content}>
        <TextField select fullWidth variant="outlined" size="small" label="Enviar pelo WhatsApp" value={whatsappId} onChange={event => edit(setWhatsappId)(event.target.value)} disabled={busy || loadingConnections} helperText={loadingConnections ? "Carregando conexões…" : !whatsApps.length ? "Adicione uma conexão em Conexões para começar." : "Escolha o número da empresa que enviará a mensagem."}>
          <MenuItem value="" disabled>Selecione uma conexão</MenuItem>
          {whatsApps.map(item => <MenuItem key={item.id} value={item.id} disabled={item.status !== "CONNECTED"}>{item.name}{item.isTest ? " · Simulado" : ""}{item.status !== "CONNECTED" ? " · Desconectado" : ""}</MenuItem>)}
        </TextField>
        <div className={classes.search}>
          <Autocomplete freeSolo autoHighlight options={options} value={contact} inputValue={search} loading={searching} disabled={busy}
            getOptionLabel={option => typeof option === "string" ? option : contactLabel(option)}
            getOptionSelected={(option, value) => option.id === value.id} filterOptions={items => items}
            onChange={(_, value) => {
              const next = value && typeof value === "object" ? value : null;
              searchGeneration.current++;
              const label = next ? contactLabel(next) : typeof value === "string" ? value : "";
              selectionRef.current = { contact: next, search: label };
              edit(setContact)(next); setSearch(label);
              setOptions([]);
            }}
            onInputChange={(_, value, reason) => {
              if (reason === "input" || reason === "clear") {
                searchGeneration.current++;
                selectionRef.current = { contact: null, search: value };
                setOptions([]); setContact(null); edit(setSearch)(value);
              }
            }}
            renderOption={option => <div className={classes.option}><ContactAvatar contact={option} style={{ width: 32, height: 32 }} /><div><div className={classes.optionName}>{contactDisplayName(option)}</div><div className={classes.optionNumber}>{contactNumber(option) || "Telefone ainda não identificado"}</div></div></div>}
            renderInput={params => <TextField {...params} onKeyDown={event => { if (event.key === "Enter") event.preventDefault(); }} autoFocus label="Nome ou número do WhatsApp" placeholder="Pesquise ou digite +55 11 99999-9999" variant="outlined" size="small" helperText={contact ? (contactNumber(contact) ? "Contato selecionado. A conversa anterior será preservada." : "Este contato ainda não tem telefone identificado. Digite o número real para enviar.") : "Para um número novo, inclua o código do país e o DDD."} InputProps={{ ...params.InputProps, endAdornment: <>{searching && <CircularProgress size={17} />}{params.InputProps.endAdornment}</> }} />}
          />
          <Button size="small" className={classes.sync} startIcon={importing ? <CircularProgress size={15} /> : <SyncIcon />} onClick={handleSync} disabled={!connected || busy}>Sincronizar contatos deste WhatsApp</Button>
          {notice && <Typography role="status" className={classes.notice}>{notice}</Typography>}
        </div>
        {!contact && validNumber && <TextField label="Nome do contato (opcional)" value={name} onChange={event => edit(setName)(event.target.value)} variant="outlined" size="small" fullWidth disabled={busy} inputProps={{ maxLength: 50 }} />}
        <TextField label="Sua mensagem" placeholder="Olá! Como podemos ajudar?" multiline rows={3} rowsMax={6} variant="outlined" fullWidth value={body} onChange={event => edit(setBody)(event.target.value)} disabled={busy} inputProps={{ maxLength: 4096 }} helperText={`${body.length}/4096 · O atendimento será aberto após o envio.`} />
        <div className={classes.history}>
          <FormControlLabel style={{ marginRight: 0 }} control={<Checkbox color="primary" checked={syncHistory} disabled={busy} onChange={event => edit(setSyncHistory)(event.target.checked)} />} label={<span className={classes.historyLabel}><HistoryIcon style={{ fontSize: 17, verticalAlign: "middle", marginRight: 5 }} />Trazer histórico disponível do WhatsApp</span>} />
          <Typography className={classes.note}>As mensagens já registradas no sistema são preservadas. O WhatsApp pode disponibilizar apenas parte das conversas anteriores.</Typography>
        </div>
        {error && <div role="alert" className={classes.error}>{error}{failedTicketId && <Button color="primary" size="small" onClick={() => { onClose(); history.push(`/tickets/${failedTicketId}`); }}>Abrir atendimento</Button>}</div>}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button type="submit" color="primary" variant="contained" disableElevation disabled={busy || !connected || !validRecipient || !body.trim()} startIcon={sending ? <CircularProgress size={17} color="inherit" /> : <SendIcon />}>{sending ? "Enviando…" : "Enviar e abrir conversa"}</Button>
      </DialogActions>
    </form>
  </Dialog>;
}
